// Education narrative templates for fallback content
// Used when AI narratives are not available

/**
 * Generate executive summary for education
 */
export function generateEducationExecutiveSummary(location, stats) {
  const {
    totalInstitutions,
    avgPupilTeacherRatio,
    avgInfrastructure,
    criticalGaps,
    genderParityIndex,
  } = stats

  if (totalInstitutions === 0) {
    return `No educational institution data is currently available for ${location.name}.`
  }

  const ratioStatus = avgPupilTeacherRatio <= 40 ? 'meets' : 'exceeds'
  const infrastructureStatus =
    avgInfrastructure >= 70
      ? 'strong'
      : avgInfrastructure >= 50
        ? 'adequate'
        : 'limited'

  const genderStatus =
    genderParityIndex >= 0.95 && genderParityIndex <= 1.05
      ? 'balanced'
      : genderParityIndex < 0.95
        ? 'favoring boys'
        : 'favoring girls'

  return `${location.name} has ${totalInstitutions} educational ${totalInstitutions === 1 ? 'institution' : 'institutions'} with an average pupil-teacher ratio of ${avgPupilTeacherRatio}:1 (${ratioStatus} national standard of 40:1) and ${infrastructureStatus} infrastructure (${avgInfrastructure}% readiness). Gender parity is ${genderStatus} (index: ${genderParityIndex.toFixed(2)}). ${criticalGaps > 0 ? `${criticalGaps} critical infrastructure ${criticalGaps === 1 ? 'gap' : 'gaps'} ${criticalGaps === 1 ? 'has' : 'have'} been identified.` : 'No critical infrastructure gaps identified.'}`
}

/**
 * Section templates for education narrative
 */
export const educationSectionTemplates = {
  institutionDistribution: {
    title: 'The Education Landscape',
    description: metrics =>
      `${metrics.totalInstitutions} educational ${metrics.totalInstitutions === 1 ? 'institution' : 'institutions'} distributed across different types and ownership models. The distribution shows the mix of educational infrastructure available in this location.`,
  },

  infrastructureStatus: {
    title: 'Learning Environment & Facilities',
    description: metrics => {
      const gaps = metrics.infrastructureGaps || []

      if (gaps.length === 0) {
        return 'Infrastructure assessment shows adequate readiness across all key categories.'
      }

      const highSeverityGaps = gaps.filter(g => g.severity === 'high')

      if (highSeverityGaps.length > 0) {
        return `Critical infrastructure gaps identified: ${highSeverityGaps.map(g => `${g.name} (${g.percentage}%)`).join(', ')}. Addressing these gaps is essential for improving learning conditions.`
      }

      return `Infrastructure assessment reveals ${gaps.length} area${gaps.length === 1 ? '' : 's'} requiring improvement: ${gaps.map(g => g.name).join(', ')}.`
    },
  },

  teacherQualifications: {
    title: 'Teaching Workforce Quality',
    description: metrics => {
      const total = metrics.teacherQualifications?.total || 0
      const degree = metrics.teacherQualifications?.degree || 0
      const diploma = metrics.teacherQualifications?.diploma || 0
      const certificate = metrics.teacherQualifications?.certificate || 0

      if (total === 0) {
        return 'Teacher qualification data is not available for schools in this location.'
      }

      const degreePercent = Math.round((degree / total) * 100)

      return `Total teaching workforce: ${total} teachers. Qualifications: ${degree} degree-holders (${degreePercent}%), ${diploma} diploma-holders, ${certificate} certificate-holders. Higher qualification levels correlate with better learning outcomes.`
    },
  },

  educationRatios: {
    title: 'Resource Adequacy Indicators',
    description: metrics => {
      const ratios = metrics.educationRatios || {}
      const ptrStatus =
        (ratios.pupilTeacherRatio || 0) <= 40 ? 'meets' : 'exceeds'

      return `Key resource ratios analysis: Pupil-Teacher ${ratios.pupilTeacherRatio || 'N/A'}:1 (${ptrStatus} 40:1 standard), Pupil-Classroom ${ratios.pupilClassroomRatio || 'N/A'}:1, Pupil-Toilet ${ratios.pupilToiletRatio || 'N/A'}:1. These indicators measure resource adequacy and learning environment quality.`
    },
  },

  genderEquality: {
    title: 'Gender Parity & Inclusion',
    description: metrics => {
      const genderData = metrics?.genderParity || {}
      const index =
        typeof genderData.parityIndex === 'number'
          ? genderData.parityIndex
          : 1.0
      const boys = genderData.boysCount || 0
      const girls = genderData.girlsCount || 0

      if (boys === 0 && girls === 0) {
        return 'Gender enrollment data is not available for this location.'
      }

      const status =
        index >= 0.95 && index <= 1.05
          ? 'excellent gender balance'
          : index < 0.95
            ? 'gender gap favoring boys'
            : 'gender gap favoring girls'

      return `Enrollment: ${boys.toLocaleString()} boys, ${girls.toLocaleString()} girls. Gender Parity Index: ${index.toFixed(2)} indicates ${status}. WHO recommends 0.97-1.03 for gender equity.`
    },
  },

  locationRanking: {
    title: type => {
      const typeMap = {
        subcounties: 'Subcounty',
        parishes: 'Parish',
        villages: 'Village',
      }
      return `Education Performance Across ${typeMap[type] || 'Location'}s`
    },
    description: comparisons => {
      if (
        !comparisons ||
        !comparisons.rankings ||
        comparisons.rankings.length === 0
      ) {
        return 'Sub-location comparison data is not available.'
      }

      const topLocation = comparisons.rankings[0]
      const bottomLocation =
        comparisons.rankings[comparisons.rankings.length - 1]
      const scoreDiff =
        topLocation.compositeScore - bottomLocation.compositeScore

      return `Comparative analysis of ${comparisons.rankings.length} ${comparisons.type}. Top-ranked: ${topLocation.name} (education quality score: ${topLocation.compositeScore}). Performance gap: ${scoreDiff} points between highest and lowest ranked locations.`
    },
  },
}

/**
 * Generate key stats cards for education overview
 */
// eslint-disable-next-line no-unused-vars
export function generateEducationKeyStats(stats, location) {
  const keyStats = []

  if (stats.totalInstitutions > 0) {
    keyStats.push({
      label: 'Schools',
      value: stats.totalInstitutions,
      icon: '🏫',
    })

    keyStats.push({
      label: 'Avg. Pupil-Teacher Ratio',
      value: `${stats.avgPupilTeacherRatio}:1`,
      icon: '👨‍🏫',
      status: stats.avgPupilTeacherRatio <= 40 ? 'good' : 'poor',
    })

    keyStats.push({
      label: 'Infrastructure Readiness',
      value: `${stats.avgInfrastructure}%`,
      icon: '🏗️',
      status:
        stats.avgInfrastructure >= 70
          ? 'good'
          : stats.avgInfrastructure >= 50
            ? 'moderate'
            : 'poor',
    })

    if (stats.genderParityIndex) {
      keyStats.push({
        label: 'Gender Parity Index',
        value: stats.genderParityIndex.toFixed(2),
        icon: '⚖️',
        status:
          stats.genderParityIndex >= 0.95 && stats.genderParityIndex <= 1.05
            ? 'good'
            : 'moderate',
      })
    }
  }

  return keyStats
}

/**
 * Generate priority alerts for education
 */
export function generateEducationPriorityAlerts(metrics) {
  const alerts = []

  // Check for critical infrastructure gaps
  const criticalGaps = (metrics.infrastructureGaps || []).filter(
    gap => gap.severity === 'high'
  )
  if (criticalGaps.length > 0) {
    alerts.push({
      type: 'warning',
      text: `${criticalGaps.length} critical infrastructure ${criticalGaps.length === 1 ? 'gap' : 'gaps'}: ${criticalGaps.map(g => g.name).join(', ')}`,
    })
  }

  // Check for poor pupil-teacher ratio
  const ptr = metrics.educationRatios?.pupilTeacherRatio || 0
  if (ptr > 45) {
    alerts.push({
      type: 'alert',
      text: `Pupil-teacher ratio (${ptr}:1) significantly exceeds national standard of 40:1`,
    })
  }

  // Check for gender parity
  const genderIndex = metrics.genderParity?.parityIndex || 1.0
  if (genderIndex < 0.9 || genderIndex > 1.1) {
    alerts.push({
      type: 'info',
      text: `Gender parity index (${genderIndex.toFixed(2)}) indicates enrollment imbalance requiring attention`,
    })
  }

  // Check for good performance
  const avgInfra =
    metrics.infrastructureAvailability &&
    Object.values(metrics.infrastructureAvailability).reduce(
      (sum, v) => sum + v,
      0
    ) / Object.keys(metrics.infrastructureAvailability).length

  if (avgInfra >= 75 && ptr <= 40 && alerts.length === 0) {
    alerts.push({
      type: 'success',
      text: `Strong infrastructure and teacher coverage across institutions`,
    })
  }

  return alerts
}
